import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, TrendingUp } from "lucide-react";
import { RESULTS_CASES, RESULTS_STATS } from "@/lib/data";

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

// Curated showcase: 3 cases highlighting variety (studio coast / villa family / longère heritage)
const FEATURED_IDS = ["studio-saint-cast", "villa-familiale-cancale", "longere-dinan"];
const CASES_BY_ID = Object.fromEntries(RESULTS_CASES.map((c) => [c.id, c]));
const FEATURED_CASES = FEATURED_IDS.map((id) => CASES_BY_ID[id]).filter(Boolean);

const ResultsTeaser = () => (
  <section
    className="py-20 lg:py-28 bg-white relative overflow-hidden"
    data-testid="results-teaser"
  >
    {/* Decorative orbs */}
    <div
      aria-hidden="true"
      className="absolute -top-20 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none opacity-50"
      style={{
        background:
          "radial-gradient(circle, rgba(197,138,63,0.14) 0%, transparent 65%)",
      }}
    />

    <div className="container-tm relative">
      {/* Header, 2 columns */}
      <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
        <div className="lg:col-span-7">
          <p className="overline mb-4 inline-flex items-center gap-2">
            <TrendingUp size={12} strokeWidth={2} className="text-gold" />
            Résultats clients
          </p>
          <h2 className="font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            Des chiffres réels,
            <br />
            <em className="text-gold">avant & après nous.</em>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="font-sans text-base text-ink/65 leading-relaxed">
            Pas du marketing : 3 cas réels parmi nos clients de la Côte
            d'Émeraude et du Morbihan, avec leurs chiffres avant / après. Cas
            complets et 5 autres études de cas sur la page dédiée.
          </p>
        </div>
      </div>

      {/* Quick stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mb-12 lg:mb-16 py-6 px-4 lg:px-8 bg-cream rounded-3xl border border-gold/15">
        {RESULTS_STATS.map((s, i) => (
          <div
            key={s.label}
            data-testid={`teaser-stat-${i}`}
            className="text-center md:border-l md:first:border-l-0 md:border-ink/10"
          >
            <p
              className="font-serif italic font-light text-gold leading-none tabular-nums"
              style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}
            >
              {s.value}
            </p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-ink/60">
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* 3 featured cases */}
      <div className="grid md:grid-cols-3 gap-5 lg:gap-6 mb-12">
        {FEATURED_CASES.map((c, i) => (
          <Link
            to="/resultats"
            key={c.id}
            data-testid={`teaser-case-${c.id}`}
            className="group relative block bg-cream rounded-3xl overflow-hidden shadow-soft border border-ink/5 hover:shadow-lift hover:-translate-y-1 transition-all duration-500"
          >
            {/* Image */}
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={c.image}
                alt={`${c.title}, ${c.city}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
              />
              {/* City tag */}
              <span className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold text-ink">
                {c.city}
              </span>
              {/* Delta badge */}
              <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-gold text-ink rounded-full px-3 py-1.5 shadow-soft">
                <ArrowUpRight size={11} strokeWidth={2.4} />
                <span className="font-serif text-sm font-medium tabular-nums">
                  +{c.delta}%
                </span>
              </span>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1.5">
                {c.type}
              </p>
              <h3 className="font-serif text-lg text-ink leading-snug mb-4">
                {c.title}
              </h3>

              {/* Before / After numbers compact */}
              <div
                className="flex items-center gap-3 text-sm pt-4 border-t border-ink/8"
                data-testid={`teaser-comparison-${c.id}`}
              >
                <div className="flex-1">
                  <p className="text-[9px] uppercase tracking-[0.22em] text-ink/45 font-semibold mb-0.5">
                    Avant
                  </p>
                  <p className="font-serif text-ink/60 tabular-nums">
                    {formatEUR(c.before.revenueYear)}
                  </p>
                </div>
                <ArrowRight size={14} className="text-gold shrink-0" />
                <div className="flex-1">
                  <p className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold mb-0.5">
                    Après
                  </p>
                  <p className="font-serif text-gold tabular-nums font-medium">
                    {formatEUR(c.after.revenueYear)}
                  </p>
                </div>
              </div>

              <span className="mt-5 inline-flex items-center gap-1.5 text-gold text-[11px] uppercase tracking-[0.22em] font-semibold group-hover:gap-2.5 transition-all duration-300">
                Lire le cas complet
                <ArrowRight size={12} strokeWidth={2} />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* CTA towards full page */}
      <div className="text-center">
        <Link
          to="/resultats"
          data-testid="teaser-see-all"
          className="inline-flex items-center gap-2 bg-ink text-cream font-semibold px-7 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm"
        >
          Voir les 5 cas clients complets <ArrowRight size={15} />
        </Link>
        <p className="mt-4 text-xs text-ink/45">
          Studios, T2, T3, maisons familiales, mobile-homes, tous les profils.
        </p>
      </div>
    </div>
  </section>
);

export default ResultsTeaser;
